import * as React from 'react';
import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import history from '../Shared/history';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

// component and helper Activities
import Dashboard from './Overview/Dashboard';
import DataSources from './DataSources/DataSources';
import { workspaceTabs } from './helpers/WorkspaceHelper';
import { AppContext } from '../../containers/AppProviderWrapper';
import { workspaceActions } from './WorkspaceActions';
import { IWorkspaceContextProps } from '../../common/model/WorkspaceModels';
import Settings from './Settings/Settings';
import DashboardWrapper from './Dashboard/DashboardWrapper';
import * as caseActions from './Case/CaseActions';
import { auditActions } from '../Audit/AuditActions';
import AuditDrawer from '../Audit/AuditDrawer';
import * as dashboardActions from './Dashboard/DashboardActions';
import Security from './Security/Security';

// mui
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { CircularProgress } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import CloseIcon from '@mui/icons-material/Close';
import Paper from '@mui/material/Paper';
import ActivityIcon2 from '../../assets/img/ActivityIcon2.svg';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const WrapperContext = createContext<IWorkspaceContextProps>(undefined!);
const Workspace: React.FC = () => {
    const [value, setValue] = useState<number>(0);
    const { currentPath } = useContext(AppContext);
    const [openAudit, setOpenAudit] = useState(false);
    const [pageLimit] = useState<number>(10);
    const [offset] = useState<number>(0);
    const [sortField] = useState<string>('lastUpdated');
    const [sortDirection] = useState<string>('DESC');
    const location = useLocation();
    const dispatch = useDispatch();
    const currentWorkspace = useSelector(
        (state: any) => state.workspace.currentWorkspace
    );
    const workspaceLoading = useSelector(
        (state: any) => state.workspace.workspaceLoading
    );
    const userRoleLoading = useSelector(
        (state: any) => state.workspace.workspaceUserRoleLoading
    );
    const userRole = useSelector(
        (state: any) => state.workspace.workspaceUserRole
    );
    const { user } = useAuth0();

    useEffect(() => {
        dispatch(caseActions?.resetSelectedCase());
    }, [dispatch, value]);

    // cleanup workspace and dashboard data when leaving a selected workspace
    useEffect(() => {
        return () => {
            // dispatch(workspaceActions?.resetWorkspace());
            dispatch(dashboardActions?.reset());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!currentWorkspace || (currentWorkspace && !currentWorkspace.id)) {
            const id = location.pathname.split('/').pop().split('&')[0];
            const configs = [
                'case-configuration',
                'saved-filters-configuration',
            ];
            dispatch(workspaceActions?.getWorkspace(id));
            dispatch(workspaceActions?.getWorkspaceUserRole(id, user.name));
            dispatch(workspaceActions?.getWorkspaceQueryData('WORKSPACE', id));
            dispatch(
                auditActions?.getActivities(
                    id,
                    pageLimit,
                    offset,
                    sortField,
                    sortDirection,
                    false
                )
            );
            // eslint-disable-next-line array-callback-return
            configs.map((config) => {
                dispatch(
                    workspaceActions.getWorkspaceConfiguration(id, config)
                );
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, currentWorkspace]);

    useEffect(() => {
        if (location && location.search.includes('?id')) {
            const id = location.search.split('=')[1].split('&')[0];
            const index = workspaceTabs.map((i) => i.param).indexOf(id);
            setValue(index);
        } else {
            history.push(`${currentPath}?id=overview`);
        }
    }, [location, currentPath]);

    const handleOpenAudit = () => {
        setOpenAudit(!openAudit);
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        history.push(`${currentPath}?id=${workspaceTabs[newValue].param}`);
    };

    const handleRoleCheck = (component: ReactNode, index: number) => {
        if (value === index) {
            if (workspaceTabs[index].roles.includes(userRole)) {
                return component;
            } else {
                setValue(0);
                history.push(`${currentPath}?id=overview`);
            }
        }
    };

    if (
        userRoleLoading ||
        !userRole ||
        workspaceLoading ||
        !currentWorkspace ||
        !currentWorkspace.id
    )
        return (
            <Box
                sx={{
                    width: '90vw',
                    display: 'flex',
                    justifyContent: 'center',
                    flexGrow: 1,
                }}
            >
                <CircularProgress color={'secondary'} size={40} />
            </Box>
        );

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container justifyContent={'flex-end'}>
                        <Grid item>
                            <Tooltip
                                title={`${
                                    openAudit ? 'Close' : 'Open'
                                } Activity Log`}
                            >
                                <Fab
                                    size="small"
                                    color="secondary"
                                    aria-label="audit"
                                    onClick={handleOpenAudit}
                                    sx={{
                                        position: 'fixed',
                                        top: (theme) => theme.spacing(10),
                                        right: (theme) => theme.spacing(2),
                                    }}
                                >
                                    {openAudit ? (
                                        <CloseIcon />
                                    ) : (
                                        <img
                                            src={ActivityIcon2}
                                            alt=""
                                            height={'70%'}
                                        />
                                    )}
                                </Fab>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={openAudit ? 9 : 12}>
                    <Typography
                        variant={'h5'}
                        sx={{ fontWeight: 'bold', pb: 2, pt: 1 }}
                    >
                        {currentWorkspace && currentWorkspace.name}
                    </Typography>
                    <Box>
                        <Paper elevation={1}>
                            <Tabs
                                value={value}
                                onChange={handleChange}
                                aria-label="basic tabs example"
                                sx={{
                                    '& .MuiTab-root.Mui-selected': {
                                        backgroundColor: `#325599 !important`,
                                        color: `#fff !important`,
                                    },
                                }}
                            >
                                {/* eslint-disable-next-line array-callback-return */}
                                {workspaceTabs.map((tab: any, i: number) => {
                                    if (tab.roles.includes(userRole)) {
                                        return (
                                            <Tab
                                                label={tab.label}
                                                {...a11yProps(i)}
                                                key={tab.label}
                                            />
                                        );
                                    }
                                })}
                            </Tabs>
                        </Paper>
                    </Box>
                    <WrapperContext.Provider
                        value={{
                            currentWorkspace,
                            handleChange,
                        }}
                    >
                        <TabPanel value={value} index={0}>
                            <Dashboard />
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <DashboardWrapper />
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                            {handleRoleCheck(<DataSources />, 2)}
                        </TabPanel>
                        <TabPanel value={value} index={3}>
                            {handleRoleCheck(<Settings />, 3)}
                        </TabPanel>
                        <TabPanel value={value} index={4}>
                            {handleRoleCheck(<Security />, 4)}
                        </TabPanel>
                    </WrapperContext.Provider>
                </Grid>
                {openAudit && (
                    <Grid item xs={3}>
                        <Grid container>
                            <Grid item xs={12}>
                                <AuditDrawer
                                    workspaceId={
                                        location.pathname
                                            .split('/')
                                            .pop()
                                            .split('&')[0]
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default Workspace;
